import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Clock3, Plane, RefreshCcw, ShieldAlert, MapPinned, MapPin, TimerReset } from 'lucide-react';
import api from '../../services/api';
import CityScreen from './CityScreen';
import { useCityContext } from './useCityContext';
import { CITY_TYPES } from './cityUtils';

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${String(secs).padStart(2, '0')}s`;
  }
  return `${secs}s`;
}

function formatEta(dateValue) {
  if (!dateValue) return 'N/A';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('es-ES', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

function getRouteTravelSummary(route) {
  if (!route?.quote) return '';
  return `${route.quote.travelMinutes} min | ${route.quote.costGold} oro`;
}

export default function CityTransport() {
  const { loading, error, context, cityTypeLabel, reloadContext } = useCityContext();
  const [transportState, setTransportState] = useState({
    bot: null,
    city: null,
    transport: null,
    loadout: null,
    inventory: null
  });
  const [routes, setRoutes] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [pageError, setPageError] = useState('');
  const [pageNotice, setPageNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDispatchOpen, setConfirmDispatchOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  const previousTransportId = useRef(null);

  const activeRoute = useMemo(
    () => routes.find((route) => Number(route.id) === Number(selectedRouteId)) || routes[0] || null,
    [routes, selectedRouteId]
  );

  const canUseTransport = Boolean(context.access?.canUseTransport);
  const currentCity = transportState.city || context.city;
  const currentLoadout = transportState.loadout || null;
  const loadoutCheck = currentLoadout ? {
    canLeave: Boolean(currentLoadout.canDepart ?? currentLoadout.canLeave ?? false),
    missing: Array.isArray(currentLoadout.missing) ? currentLoadout.missing : []
  } : null;
  const isTransporting = Boolean(transportState.transport);
  const currentCityType = Number(currentCity?.city_type ?? context.city?.city_type ?? -1);
  const currentCityLabel = CITY_TYPES[currentCityType] || cityTypeLabel || 'Asentamiento';
  const transportArrivalMs = transportState.transport?.arrival_at ? new Date(transportState.transport.arrival_at).getTime() : 0;
  const liveTransportRemainingSeconds = transportArrivalMs
    ? Math.max(0, Math.ceil((transportArrivalMs - now) / 1000))
    : Number(transportState.transport?.seconds_remaining || 0);

  async function loadTransportStatus() {
    const response = await api.get('/robots/my-transport/status');
    const nextState = response.data || {};
    const nextTransportId = Number(nextState.transport?.id || 0) || null;
    const hadTransport = Boolean(previousTransportId.current);

    setTransportState(nextState);

    if (hadTransport && !nextTransportId) {
      setPageNotice('Tu bot ha aterrizado y el contexto de ciudad se ha actualizado.');
      await reloadContext(true).catch(() => {});
      previousTransportId.current = null;
      return false;
    }

    previousTransportId.current = nextTransportId;
    return Boolean(nextTransportId);
  }

  async function loadRoutes() {
    const response = await api.get('/robots/my-transport/routes');
    const nextRoutes = Array.isArray(response.data?.routes) ? response.data.routes : [];
    setRoutes(nextRoutes);
    setSelectedRouteId((current) => {
      if (current && nextRoutes.some((route) => Number(route.id) === Number(current))) {
        return current;
      }
      const firstAllowed = nextRoutes.find((route) => route.canTravel !== false);
      return (firstAllowed || nextRoutes[0] || null)?.id || null;
    });
    return nextRoutes;
  }

  async function refreshTransportData() {
    setPageError('');
    try {
      const transporting = await loadTransportStatus();
      if (!transporting && canUseTransport) {
        await loadRoutes();
      } else if (transporting) {
        setRoutes([]);
      }
    } catch (err) {
      const message = err.response?.data?.error || 'No se pudo cargar el transporte aéreo';
      setPageError(message);
      setRoutes([]);
    }
  }

  useEffect(() => {
    let mounted = true;
    async function bootstrap() {
      if (!mounted) return;
      await refreshTransportData();
    }
    bootstrap();

    return () => {
      mounted = false;
    };
  }, [context.city?.id, context.bot?.id, canUseTransport]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!previousTransportId.current) return;
      refreshTransportData().catch(() => {});
    }, 5000);

    return () => clearInterval(timer);
  }, [canUseTransport, context.city?.id, context.bot?.id]);

  const transportRemainingSeconds = liveTransportRemainingSeconds;
  const transportTravelSeconds = Math.max(1, Number(transportState.transport?.travel_minutes || 0) * 60);
  const transportProgress = isTransporting
    ? Math.min(100, Math.max(0, ((transportTravelSeconds - transportRemainingSeconds) / transportTravelSeconds) * 100))
    : 0;
  const destinationName = transportState.transport?.destination_city_name || 'Destino';
  const transportDestinationLabel = transportState.transport
    ? `${transportState.transport.destination_city_name || 'Destino'} (${CITY_TYPES[Number(transportState.transport.destination_city_type || 0)] || 'Ciudad'})`
    : '';
  const currentBotClanId = Number(context.bot?.clan || 0);

  async function handleDispatch() {
    if (!activeRoute) return;
    setConfirmDispatchOpen(false);
    setBusy(true);
    setPageError('');
    try {
      const response = await api.post('/robots/my-transport/dispatch', {
        destinationCityId: activeRoute.id
      });
      const nextTransport = response.data?.transport || null;
      setTransportState((current) => ({
        ...current,
        transport: nextTransport
      }));
      previousTransportId.current = Number(nextTransport?.id || 0) || null;
      setRoutes([]);
      setPageNotice(response.data?.message || 'Viaje programado');
      await reloadContext(true).catch(() => {});
    } catch (err) {
      setPageError(err.response?.data?.error || 'No se pudo iniciar el transporte aéreo');
    } finally {
      setBusy(false);
    }
  }

  function openDispatchConfirm() {
    if (!activeRoute?.canTravel || busy || isTransporting || !loadoutCheck?.canLeave) return;
    setConfirmDispatchOpen(true);
  }

  if (loading) {
    return <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">Cargando transporte aéreo...</div>;
  }

  if (error) {
    return <div className="min-h-screen bg-[#04060d] text-rose-200 flex items-center justify-center">{error}</div>;
  }

  return (
    <CityScreen
      context={context}
      cityTypeLabel={cityTypeLabel}
      title={`Transporte - ${currentCity?.name || context.city?.name || 'Ciudad'}`}
      subtitle="Traslado aéreo entre ciudades y capitales con espera programada."
    >
      {pageNotice && (
        <section className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200">
          {pageNotice}
        </section>
      )}

      {!canUseTransport && (
        <section className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 text-amber-100 space-y-2">
          <div className="flex items-center gap-2 font-bold text-amber-200">
            <ShieldAlert size={18} /> Transporte aéreo no disponible
          </div>
          <p className="text-sm">
            Solo hay vuelos desde ciudades y capitales. Este bot está en un {currentCityLabel.toLowerCase()}.
          </p>
        </section>
      )}

      {pageError && (
        <section className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-100">
          {pageError}
        </section>
      )}

      <section className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-4">
        <div className="rounded-2xl border border-cyan-500/30 bg-[#08101f] p-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-cyan-300 flex items-center gap-2">
                <Plane size={20} /> Bot de transporte
              </h2>
              <p className="text-sm text-slate-300 mt-1">
                Vuelos directos entre ciudades y capitales. El embarque no es instantaneo.
              </p>
            </div>
            <button
              type="button"
              onClick={() => refreshTransportData().catch(() => {})}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40 px-3 py-1.5 text-xs text-cyan-100 hover:border-cyan-300 hover:text-cyan-50"
            >
              <RefreshCcw size={14} /> Actualizar
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl border border-cyan-500/20 bg-[#050913] p-4">
              <p className="text-[11px] uppercase tracking-[0.35em] text-cyan-400/80">Origen</p>
              <p className="mt-2 font-bold text-cyan-100">{currentCity?.name || 'Sin ciudad'}</p>
              <p className="text-xs text-slate-400">{currentCityLabel}</p>
            </div>
            <div className="rounded-xl border border-cyan-500/20 bg-[#050913] p-4">
              <p className="text-[11px] uppercase tracking-[0.35em] text-cyan-400/80">Loadout</p>
              <p className="mt-2 font-bold text-cyan-100">
                {loadoutCheck?.canLeave ? 'Listo para despegar' : 'Faltan elementos'}
              </p>
              <p className="text-xs text-slate-400">
                {loadoutCheck?.canLeave ? 'Mochila y bateria activas.' : loadoutCheck?.missing?.join(' | ') || 'Sin datos'}
              </p>
            </div>
            <div className="rounded-xl border border-cyan-500/20 bg-[#050913] p-4">
              <p className="text-[11px] uppercase tracking-[0.35em] text-cyan-400/80">Estado</p>
              <p className="mt-2 font-bold text-cyan-100">{isTransporting ? 'En tránsito' : 'En tierra'}</p>
              <p className="text-xs text-slate-400">
                {isTransporting ? `${formatDuration(transportRemainingSeconds)} restantes` : 'Selecciona destino y confirma.'}
              </p>
            </div>
          </div>

          {isTransporting ? (
            <div className="rounded-2xl border border-cyan-500/30 bg-[#050913] p-4 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-cyan-200 flex items-center gap-2">
                    <TimerReset size={18} /> Viaje en curso
                  </h3>
                  <p className="text-sm text-slate-300">
                    Ruta aérea hacia <span className="font-bold text-cyan-100">{transportDestinationLabel || destinationName}</span>.
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-[0.3em] text-cyan-400/80">Llega</p>
                  <p className="font-bold text-cyan-100">{formatEta(transportState.transport?.arrival_at)}</p>
                </div>
              </div>

              <div className="h-3 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-sky-500 transition-all"
                  style={{ width: `${transportProgress}%` }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <p className="text-xs uppercase tracking-[0.25em] text-cyan-400/80">Salida</p>
                  <p className="text-cyan-100 font-bold">{formatEta(transportState.transport?.departure_at)}</p>
                </div>
                <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <p className="text-xs uppercase tracking-[0.25em] text-cyan-400/80">Tiempo total</p>
                  <p className="text-cyan-100 font-bold">{formatDuration(transportTravelSeconds)}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-cyan-200">Destinos disponibles</h3>
                  <p className="text-sm text-slate-400">Solo ciudades y capitales. El vuelo exige espera real.</p>
                </div>
                <div className="text-xs text-slate-400 flex items-center gap-2">
                  <MapPinned size={14} /> {routes.length} rutas
                </div>
              </div>

              {!canUseTransport ? (
                <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5 text-sm text-amber-100">
                  Este bot no puede usar transporte aéreo desde una ciudad que no sea capital o ciudad.
                </div>
              ) : routes.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {routes.map((route) => {
                    const selected = Number(route.id) === Number(activeRoute?.id);
                    const blocked = route.canTravel === false;
                    const sameClan = Number(route.clan || 0) === currentBotClanId;
                    return (
                      <button
                        key={route.id}
                        type="button"
                        onClick={() => {
                          if (blocked) return;
                          setSelectedRouteId(route.id);
                        }}
                        className={`text-left rounded-2xl border p-4 transition-all ${
                          blocked
                            ? 'border-red-500/50 bg-red-500/10 opacity-90'
                            : selected
                              ? 'border-cyan-300 bg-cyan-500/10 shadow-[0_0_24px_rgba(34,211,238,0.12)]'
                              : 'border-cyan-500/20 bg-[#050913] hover:border-cyan-400/50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <h4 className="text-lg font-bold text-cyan-100">{route.name}</h4>
                            <p className="text-xs text-slate-400">
                              {route.city_type_label} | {route.sector_name} | Clan: {route.clan_name}
                            </p>
                          </div>
                          <span className={`rounded-full border px-2 py-1 text-[11px] ${blocked ? 'border-red-400/40 text-red-200' : 'border-cyan-400/40 text-cyan-200'}`}>
                            {route.city_type_label}
                          </span>
                        </div>

                        <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-slate-300">
                          <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-2">
                            <p className="text-cyan-400/80 uppercase tracking-[0.25em]">Tiempo</p>
                            <p className="font-bold text-cyan-100">{route.quote.travelMinutes} min</p>
                          </div>
                          <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-2">
                            <p className="text-cyan-400/80 uppercase tracking-[0.25em]">Coste</p>
                            <p className="font-bold text-cyan-100">{route.quote.costGold} oro</p>
                          </div>
                          <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-2">
                            <p className="text-cyan-400/80 uppercase tracking-[0.25em]">ETA</p>
                            <p className="font-bold text-cyan-100">{formatEta(route.quote.arrivalAt)}</p>
                          </div>
                        </div>

                        <div className="mt-3 flex items-center justify-between gap-2">
                          <p className="text-[11px] text-slate-400">{getRouteTravelSummary(route)}</p>
                          <div className={`inline-flex items-center gap-2 text-xs ${blocked ? 'text-red-200' : 'text-cyan-200'}`}>
                            {blocked ? 'Bloqueado' : 'Seleccionar'} <ArrowRight size={14} />
                          </div>
                        </div>
                        {blocked && (
                          <p className="mt-2 text-[11px] text-red-100/90">
                            {route.blockedReason || (sameClan ? 'Destino bloqueado' : 'Solo vuelos al mismo clan')}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-700 bg-[#050913] p-5 text-sm text-slate-300">
                  No hay rutas disponibles en este momento.
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="rounded-2xl border border-cyan-500/30 bg-[#08101f] p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-xl font-bold text-cyan-300">Resumen de vuelo</h3>
              <p className="text-sm text-slate-400">Confirma un destino para preparar la salida.</p>
            </div>
            <MapPin className="text-cyan-300" size={18} />
          </div>

          {isTransporting ? (
            <>
              <div className="rounded-2xl border border-cyan-500/20 bg-[#050913] p-4">
                <p className="text-[11px] uppercase tracking-[0.35em] text-cyan-400/80">Destino actual</p>
                <h4 className="mt-2 text-2xl font-bold text-cyan-100">
                  {transportState.transport?.destination_city_name || 'Destino'}
                </h4>
                <p className="text-sm text-slate-400">
                  {CITY_TYPES[Number(transportState.transport?.destination_city_type || 0)] || 'Ciudad'} | Sector #{transportState.transport?.destination_sector_id || 0}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-cyan-400/80">Tiempo restante</p>
                  <p className="text-cyan-100 font-bold">{formatDuration(transportRemainingSeconds)}</p>
                </div>
                <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-cyan-400/80">Llegada</p>
                  <p className="text-cyan-100 font-bold">{formatEta(transportState.transport?.arrival_at)}</p>
                </div>
              </div>
            </>
          ) : activeRoute ? (
            <>
              <div className="rounded-2xl border border-cyan-500/20 bg-[#050913] p-4">
                <p className="text-[11px] uppercase tracking-[0.35em] text-cyan-400/80">Destino seleccionado</p>
                <h4 className="mt-2 text-2xl font-bold text-cyan-100">{activeRoute.name}</h4>
                <p className="text-sm text-slate-400">
                  {activeRoute.city_type_label} | {activeRoute.sector_name} | Clan: {activeRoute.clan_name}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-cyan-400/80">Tiempo total</p>
                  <p className="text-cyan-100 font-bold">{activeRoute.quote.travelMinutes} minutos</p>
                </div>
                <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-cyan-400/80">Coste</p>
                  <p className="text-cyan-100 font-bold">{activeRoute.quote.costGold} oro</p>
                </div>
              </div>

              <div className="rounded-xl border border-cyan-500/20 bg-[#050913] p-4 text-sm space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-400">Despegue</span>
                  <span className="text-cyan-100 font-bold">{formatEta(activeRoute.quote.departureAt)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-400">Llegada</span>
                  <span className="text-cyan-100 font-bold">{formatEta(activeRoute.quote.arrivalAt)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-400">Permiso</span>
                  <span className={`font-bold ${activeRoute.canTravel ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {activeRoute.canTravel ? 'OK' : 'Bloqueado'}
                  </span>
                </div>
              </div>

              <button
                type="button"
                disabled={!activeRoute?.canTravel || busy || isTransporting || !loadoutCheck?.canLeave}
                onClick={openDispatchConfirm}
                className={`w-full rounded-xl px-4 py-3 font-bold transition-all ${
                  !activeRoute?.canTravel || busy || isTransporting || !loadoutCheck?.canLeave
                    ? 'cursor-not-allowed border border-slate-700 bg-slate-800 text-slate-500'
                    : 'border border-cyan-400/50 bg-cyan-500/10 text-cyan-100 hover:border-cyan-300 hover:bg-cyan-500/15'
                }`}
              >
                {busy
                  ? 'Programando vuelo...'
                  : isTransporting
                    ? 'Ya hay un viaje activo'
                    : !loadoutCheck?.canLeave
                      ? 'Loadout incompleto'
                      : activeRoute?.canTravel === false
                        ? 'Destino bloqueado'
                      : 'Confirmar vuelo'}
              </button>
            </>
          ) : (
            <div className="rounded-2xl border border-slate-700 bg-[#050913] p-5 text-sm text-slate-300 space-y-3">
              <p>No hay un destino seleccionado todavía.</p>
              <p>Elige una ciudad o capital para ver coste y tiempo estimado.</p>
            </div>
          )}

          <div className="rounded-2xl border border-cyan-500/20 bg-[#050913] p-4 text-xs text-slate-400 space-y-2">
            <div className="flex items-center gap-2 text-cyan-200 font-bold">
              <Clock3 size={14} /> Reglas del vuelo
            </div>
            <p>1. Solo ciudades y capitales pueden tener rutas aéreas.</p>
            <p>2. El bot debe llevar mochila y batería activas para despegar.</p>
            <p>3. El vuelo tarda varios minutos y el bot queda en tránsito hasta aterrizar.</p>
          </div>
        </aside>
      </section>

      {confirmDispatchOpen && activeRoute && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-3xl border border-cyan-400/30 bg-[#08111f] shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-cyan-400/20">
              <p className="text-xs uppercase tracking-[0.25em] text-cyan-300/70">Confirmación de vuelo</p>
              <h3 className="text-2xl font-bold text-white mt-1">{activeRoute.name}</h3>
              <p className="text-sm text-slate-300 mt-2">
                Estás a punto de despegar hacia <span className="font-semibold text-cyan-200">{activeRoute.name}</span>.
                El viaje tardará {activeRoute.quote.travelMinutes} minutos y costará {activeRoute.quote.costGold} oro.
              </p>
            </div>

            <div className="p-5 space-y-3 text-sm text-slate-300">
              <p className="text-amber-200">
                Comprueba bien el destino antes de confirmar. Si te equivocas, tendrás que esperar a aterrizar para cambiar de ruta.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-700 bg-[#050913] p-3">
                  <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Destino</p>
                  <p className="font-bold text-cyan-100">{activeRoute.name}</p>
                </div>
                <div className="rounded-xl border border-slate-700 bg-[#050913] p-3">
                  <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Tiempo</p>
                  <p className="font-bold text-cyan-100">{activeRoute.quote.travelMinutes} min</p>
                </div>
              </div>
              <p className="text-xs text-slate-400">
                Mientras dure el vuelo, la ciudad quedará bloqueada y solo el transporte permanecerá disponible.
              </p>
            </div>

            <div className="px-5 pb-5 flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmDispatchOpen(false)}
                className="flex-1 rounded-xl border border-slate-600 bg-slate-800 px-4 py-3 text-slate-100 hover:bg-slate-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleDispatch}
                className="flex-1 rounded-xl border border-cyan-400 bg-cyan-600 px-4 py-3 text-white font-semibold hover:bg-cyan-500"
              >
                Sí, volar
              </button>
            </div>
          </div>
        </div>
      )}
    </CityScreen>
  );
}
